import type { ContainerList, ListAccount, ListContainer, ListTank } from "./api";
import { dayText, gallons, STATUS_TEXT } from "./accounts";
import { percentText, shortDay, visitShareText } from "./calendar";
import { floatAgrees, floatShare, floatWords } from "./fill";
import { bandText } from "./forecast";
import type { SortColumn } from "./sort";

/** One row of the Levels page: an account and its containers (SO-4115). */
export interface ContainerRow {
  account: ListAccount;
  containers: ListContainer[];
  /** The container whose sensor gives the level, else the first one. */
  container: ListContainer;
}

export type Source = "sensor" | "estimate";

export interface Level {
  text: string;
  source: Source | null;
  /** The fill share for the sort; a float switch that is not full uses the estimate. */
  share: number | null;
  state?: string;
  observedAt?: string;
  /** The estimate under a float report, or the band of an estimate, on a line of its own (rule U-5). */
  detail?: string;
  /** A float report that no longer holds the fill: too old, or a sensor out of the field (S-8). */
  old?: boolean;
  /** Why an old float report does not hold the fill, under the chip: its age, or "Not in the field". */
  note?: string;
  /** True when the note gives the age of the report, so the sensor line leaves the age out. */
  dated?: boolean;
}

const LEVEL_TEXT: Record<string, string> = { full: "Full", not_full: "Not full" };
const IN_ROTATION = new Set<ListAccount["status"]>(["NEW", "REG"]);

export const SOURCE_TEXT: Record<Source, string> = { sensor: "Sensor", estimate: "Estimate" };

const levelContainer = (containers: ListContainer[]) =>
  containers.find((c) => c.tank?.state && c.tank.in_field !== false) ??
  containers.find((c) => c.tank?.state) ??
  containers.find((c) => c.tank) ??
  containers[0];

export function rowsOf(list: ContainerList): ContainerRow[] {
  const held = new Map<string, ListContainer[]>();
  for (const container of list.containers) {
    const found = held.get(container.account_id);
    if (found) found.push(container);
    else held.set(container.account_id, [container]);
  }
  return list.accounts.flatMap((account) => {
    const containers = held.get(account.id);
    return containers ? [{ account, containers, container: levelContainer(containers) }] : [];
  });
}

/** The sensor tanks of the containers of a row, each once. */
export function rowTanks(row: ContainerRow): NonNullable<ListContainer["tank"]>[] {
  const seen = new Map<string, NonNullable<ListContainer["tank"]>>();
  for (const container of row.containers) if (container.tank && !seen.has(container.tank.id)) seen.set(container.tank.id, container.tank);
  return [...seen.values()];
}

function estimate(account: ListAccount): Level {
  const projection = account.projection;
  if (!projection || account.status === "OFF") return { text: "–", source: null, share: null };
  if (projection.off_season) return { text: "Off season", source: null, share: null };
  if (projection.today) {
    const [text, range] = splitBand(bandText(projection.today));
    return { text, detail: range, source: "estimate", share: projection.today.p50 };
  }
  const share = projection.load_share;
  if (share == null) return { text: "No pickups yet", source: null, share: null };
  return { text: `About ${percentText(Math.min(share, 1))} full`, source: "estimate", share };
}

/** "About 45% full (30–60%)" as the chip text and the band, so the chip stays on one line. */
export function splitBand(text: string): [string, string | undefined] {
  const found = /^(.*) \((.*)\)$/.exec(text);
  return found ? [found[1], found[2]] : [text, undefined];
}

/** The detail as parts; only a band in brackets, or a detail that is only a band, stays on one line. */
export function detailParts(detail: string): { text: string; band: boolean }[] {
  if (!detail.includes("(")) return [{ text: detail, band: /^\d/.test(detail) }];
  return detail
    .split(/(\([^()]*\))/)
    .filter((text) => text !== "")
    .map((text) => ({ text, band: text.startsWith("(") }));
}

const daysAgo = (at: string, now: Date) => {
  const days = Math.floor((now.getTime() - Date.parse(at)) / 86_400_000);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
};

/** Rule S-8: a sensor with no report for 2 days counts as no sensor, as in the API (QUIET_DAYS). */
export const QUIET_MS = 2 * 24 * 3600 * 1000;

/** Rule S-1: a float switch shows full or not full, never a percent; a level sensor shows its percent. */
export function levelOf(row: ContainerRow, now: Date = new Date()): Level {
  const state = row.container.tank?.state;
  const pct = state?.measurement.fill_pct;
  if (state && typeof pct === "number" && Number.isFinite(pct)) {
    return { text: `${Math.round(pct)}% full`, source: "sensor", share: pct / 100, observedAt: state.observed_at };
  }
  const level = state?.measurement.level_state;
  if (state && (level === "full" || level === "not_full")) {
    const full = level === "full";
    const guess = estimate(row.account);
    const floatPct = row.container.float_pct;
    const inField = row.container.tank?.in_field !== false;
    const fresh = now.getTime() - Date.parse(state.observed_at) < QUIET_MS;
    const held = inField && fresh;
    const agrees = guess.source === "estimate" && (!held || floatAgrees(full, floatPct, guess.share));
    const said = guess.detail ? `${guess.text} (${guess.detail})` : guess.text;
    const share = held ? floatShare(full, floatPct, guess.share) : guess.share;
    if (held) {
      const detail = agrees ? `Estimate ${said.replace(/^About/, "about")}` : undefined;
      return { text: floatWords(full, floatPct), detail, source: "sensor", share, state: level, observedAt: state.observed_at };
    }
    const why = fresh ? "the sensor is not in the field" : "the float report is too old";
    const text = `${full ? "Above" : "Below"} the float`;
    const note = fresh ? "Not in the field" : daysAgo(state.observed_at, now);
    const detail = agrees ? `Estimate ${said.replace(/^About/, "about")}, ${why}` : undefined;
    return { text, note, dated: !fresh, detail, source: "sensor", share, state: level, observedAt: state.observed_at, old: true };
  }
  if (state && level) return { text: LEVEL_TEXT[level] ?? level, source: "sensor", share: estimate(row.account).share, state: level, observedAt: state.observed_at };
  return estimate(row.account);
}

export function weeksLeftText(account: ListAccount): string {
  const left = account.projection?.weeks_left;
  if (account.projection?.off_season) return "– (off season)";
  if (left == null || !IN_ROTATION.has(account.status)) return "–";
  if (left <= 0) return "Due now";
  return left === 1 ? "1 week" : `${left} weeks`;
}

export function nextVisitText(account: ListAccount): string {
  const visit = account.projection?.next_visit;
  if (!visit || !IN_ROTATION.has(account.status)) return "–";
  const share = visitShareText(account.projection?.projected_share, account.projection?.at_visit);
  return share == null ? shortDay(visit) : `${shortDay(visit)} · ${share}`;
}

/** The newer of the last pickup (a date) and the last visit (a time), as a date. */
export function lastService(account: ListAccount): { day: string; kind: "pickup" | "visit" } | null {
  const visit = account.last_visit?.slice(0, 10) ?? null;
  const pickup = account.last_pickup;
  if (!visit && !pickup) return null;
  if (visit && (!pickup || visit > pickup)) return { day: visit, kind: "visit" };
  return { day: pickup as string, kind: "pickup" };
}

export const lastServiceText = (account: ListAccount) => {
  const last = lastService(account);
  return last ? `${dayText(last.day)} · ${last.kind}` : "–";
};

/** "2 × 110 gal", or "110 + 55 gal" for different sizes, the largest first. */
export function sizeText(containers: readonly Pick<ListContainer, "size_gal">[]): string {
  const counts = new Map<number, number>();
  for (const { size_gal } of containers) counts.set(size_gal, (counts.get(size_gal) ?? 0) + 1);
  const parts = [...counts].sort(([a], [b]) => b - a).map(([size, count]) => (count > 1 ? `${count} × ${gallons(size)}` : gallons(size)));
  return `${parts.join(" + ")} gal`;
}

export const kindText = (containers: readonly Pick<ListContainer, "kind">[]) => [...new Set(containers.map((c) => c.kind))].join(", ");

const capacity = (row: ContainerRow) => row.containers.reduce((sum, c) => sum + c.size_gal, 0);

export const CONTAINER_COLUMNS: SortColumn<ContainerRow>[] = [
  { key: "account", label: "Account", value: (r) => r.account.number },
  { key: "name", label: "Name", value: (r) => r.account.name },
  { key: "area", label: "Area", value: (r) => r.account.plan?.area },
  { key: "size", label: "Containers", value: capacity, first: "desc", num: true },
  { key: "level", label: "Level", value: (r) => levelOf(r).share, first: "desc" },
  { key: "source", label: "Source", value: (r) => levelOf(r).source, order: ["sensor", "estimate"], off: true },
  { key: "sensor", label: "Sensor", value: (r) => r.container.tank?.label },
  {
    key: "left",
    label: "Weeks left",
    value: (r) => (IN_ROTATION.has(r.account.status) ? r.account.projection?.weeks_left : null),
  },
  {
    key: "visit",
    label: "Next visit",
    value: (r) => (IN_ROTATION.has(r.account.status) ? r.account.projection?.next_visit : null),
  },
  { key: "last", label: "Last pickup or visit", value: (r) => lastService(r.account)?.day, first: "desc" },
  { key: "status", label: "Status", value: (r) => STATUS_TEXT[r.account.status], off: true },
];

/** With no sort chosen: the fullest first, then by account number. */
export function fullestFirst(rows: readonly ContainerRow[]): ContainerRow[] {
  return rows
    .map((row) => ({ row, share: levelOf(row).share }))
    .sort((a, b) => {
      if (a.share !== b.share) return a.share == null ? 1 : b.share == null ? -1 : b.share - a.share;
      return a.row.account.number.localeCompare(b.row.account.number, undefined, { numeric: true });
    })
    .map(({ row }) => row);
}

export interface TankFilter {
  q: string;
  status: ListAccount["status"] | "";
  week: number;
  source: Source | "none" | "";
  /** Show the sensor tanks that no account has. */
  unlinked: boolean;
}

export const NO_TANK_FILTER: TankFilter = { q: "", status: "", week: 0, source: "", unlinked: false };

export const isTankFiltered = (filter: TankFilter) =>
  Boolean(filter.q.trim() || filter.status || filter.week || filter.source);

const words = (q: string) => q.trim().toLowerCase().split(/\s+/).filter(Boolean);
const compact = (text: string) => text.toLowerCase().replace(/[\s-]/g, "");

function matches(text: string, q: string, number = ""): boolean {
  const lower = text.toLowerCase();
  return words(q).every((word) => lower.includes(word) || (number !== "" && compact(number).includes(compact(word))));
}

function weeksOf(account: ListAccount): number[] {
  if (!account.plan) return [];
  return [account.plan.rotation_week, ...account.plan.week_group.split("-").map(Number)];
}

export function filterRows(rows: ContainerRow[], filter: TankFilter): ContainerRow[] {
  return rows.filter((row) => {
    const { account } = row;
    const source = levelOf(row).source ?? "none";
    const sensors = rowTanks(row).map((tank) => tank.label).join(" ");
    const text = `${account.number} ${account.name} ${account.address} ${account.plan?.area ?? ""} ${sensors}`;
    return (
      (!filter.status || account.status === filter.status) &&
      (!filter.week || weeksOf(account).includes(filter.week)) &&
      (!filter.source || source === filter.source) &&
      matches(text, filter.q, account.number)
    );
  });
}

/** The sensor tanks of the second table: those of an account, and with the toggle those of no account. */
export function filterTanks(tanks: ListTank[], filter: TankFilter): ListTank[] {
  if (filter.status || filter.week || filter.source === "estimate" || filter.source === "none") return [];
  return tanks.filter((tank) => (tank.linked || filter.unlinked) && matches(`${tank.label} ${tank.devices.join(" ")}`, filter.q));
}

export const unlinkedCount = (tanks: ListTank[]) => tanks.filter((tank) => !tank.linked).length;

export const unlinkedText = (count: number) =>
  `Show ${count} ${count === 1 ? "container" : "containers"} with a sensor and no account`;

const counted = (n: number, one: string, many: string) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

export function containerSummary(rows: ContainerRow[], shown: number): string {
  const sensors = rows.filter((row) => levelOf(row).source === "sensor").length;
  const count = counted(rows.length, "account", "accounts");
  const parts = [shown === rows.length ? count : `${shown.toLocaleString("en-US")} of ${count}`];
  parts.push(counted(rows.reduce((sum, row) => sum + row.containers.length, 0), "container", "containers"));
  if (sensors) parts.push(`${sensors} with a sensor reading`);
  return parts.join(" · ");
}

export function readTankFilter(search: string): TankFilter {
  const params = new URLSearchParams(search);
  const status = params.get("status") ?? "";
  const week = Number(params.get("week"));
  const source = params.get("source") ?? "";
  return {
    q: params.get("q") ?? "",
    status: status in STATUS_TEXT ? (status as ListAccount["status"]) : "",
    week: [1, 2, 3, 4].includes(week) ? week : 0,
    source: source === "sensor" || source === "estimate" || source === "none" ? source : "",
    unlinked: params.get("unlinked") === "1",
  };
}

export function writeTankFilter(filter: TankFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.q.trim()) params.set("q", filter.q.trim());
  if (filter.status) params.set("status", filter.status);
  if (filter.week) params.set("week", String(filter.week));
  if (filter.source) params.set("source", filter.source);
  if (filter.unlinked) params.set("unlinked", "1");
  return params;
}
